import { Suspense } from 'react';
import { Outlet, NavLink, useLocation, Link } from 'react-router-dom';
import { PageLoader } from '@/components/PageLoader';
import { RouteErrorBoundary } from '@/components/AppErrorBoundary';
import { useState, useEffect } from 'react';
import {
  LayoutDashboard, Users, Boxes, Megaphone, BookOpen, UserCog, Store,
  Sparkles, Rocket, Building2, Calendar, CalendarDays, MessagesSquare, MessageSquare, Briefcase, Wallet,
  FileStack, BarChart3, BrainCircuit, ShieldCheck, ShieldAlert, Settings2, ArrowLeft, LogOut, Menu, Lock, Star, Inbox, ClipboardCheck, Gauge, Radar, Bell, Heart, Landmark,
} from 'lucide-react';
import { Logo } from '@/components/brand/Logo';
import { ThemeToggle } from '@/components/brand/ThemeToggle';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useAuth } from '@/hooks/use-auth';
import { useAdminPerms } from '@/hooks/use-admin-perms';
import { WorkspaceSwitcher } from '@/components/layout/WorkspaceSwitcher';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';

const live = [
  { to: '/app/admin', label: 'Dashboard', icon: LayoutDashboard, end: true, perm: 'dashboard' },
  { to: '/app/admin/users', label: 'User Management', icon: Users, perm: 'users' },
  { to: '/app/admin/applications', label: 'Applications', icon: Inbox, perm: 'applications' },
  { to: '/app/admin/programs', label: 'Programs & Cohorts', icon: Boxes, perm: 'programs' },
  { to: '/app/admin/lms', label: 'LMS Admin', icon: BookOpen, perm: 'lms' },
  { to: '/app/admin/lms/submissions', label: 'Submissions', icon: ClipboardCheck, perm: 'lms' },
  { to: '/app/admin/sessions', label: 'Sessions', icon: Calendar, perm: 'sessions' },
  { to: '/app/admin/mentors', label: 'Mentors', icon: Sparkles, perm: 'mentors' },
  { to: '/app/admin/marketplace', label: 'Mentor Marketplace', icon: Store, perm: 'mentors' },
  { to: '/app/admin/mentor-finance', label: 'Mentor Finance', icon: Wallet, perm: ['mentors', 'finance'] },
  { to: '/app/admin/events', label: 'Events', icon: CalendarDays, perm: 'events' },
  { to: '/app/admin/discover', label: 'Opportunities', icon: Radar, perm: 'events' },
  { to: '/app/admin/investors', label: 'Investors', icon: Building2, perm: 'investors' },
  { to: '/app/admin/capital', label: 'Bower Capital', icon: Landmark, perm: 'capital' },
  { to: '/app/admin/startups', label: 'Ventures', icon: Rocket, perm: 'startups' },
  { to: '/app/admin/career', label: 'Career Services', icon: Briefcase, perm: 'career' },
  { to: '/app/admin/match', label: 'Cofounder Match', icon: Heart, perm: 'community' },
  { to: '/app/admin/community', label: 'Community', icon: MessagesSquare, perm: 'community' },
  { to: '/app/admin/messaging', label: 'Messaging Console', icon: MessageSquare, perm: 'messaging' },
  { to: '/app/admin/ai', label: 'AI Command', icon: BrainCircuit, perm: 'ai' },
  { to: '/app/admin/finance', label: 'Finance', icon: Wallet, perm: 'finance' },
  { to: '/app/admin/vendors', label: 'Vendor & PO', icon: FileStack, perm: 'vendors' },
  { to: '/app/admin/communications', label: 'Communication Hub', icon: Bell, perm: 'communications' },
  { to: '/app/admin/reports', label: 'Reports', icon: BarChart3, perm: 'reports' },
  { to: '/app/admin/ratings', label: 'Ratings & Reviews', icon: Star, perm: 'ratings' },
  { to: '/app/admin/rbac', label: 'Roles (RBAC)', icon: ShieldCheck, perm: 'rbac' },
  { to: '/app/admin/safety', label: 'Safety Center', icon: ShieldCheck, perm: 'community' },
  { to: '/app/admin/security', label: 'Security & Ops', icon: ShieldAlert, perm: 'rbac' },
  { to: '/app/admin/engineering', label: 'Engineering', icon: Gauge, perm: 'rbac' },
  { to: '/app/admin/settings', label: 'Super Admin', icon: Settings2, perm: 'settings' },
];

const soon = [];

// Hoisted so React keeps the same component identity across AdminShell re-renders —
// defining it inline remounted the whole nav on every route change, snapping the
// sidebar scroll position back to the top (same fix as AppShell's NavList).
const AdminNav = ({ visible }) => (
  <nav className="px-3 mt-4 flex-1 overflow-y-auto scrollbar-hide space-y-6 pb-6">
    <div>
      <p className="px-3 mb-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-grotesk">Operate</p>
      <ul className="space-y-0.5">
        {visible.map((n) => (
          <li key={n.to}>
            <NavLink
              to={n.to}
              end={n.end}
              data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={({ isActive }) => cn(
                'flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors',
                isActive ? 'bg-foreground text-background' : 'text-foreground/70 hover:text-foreground hover:bg-secondary'
              )}
            >
              <n.icon className="h-4 w-4" />
              <span>{n.label}</span>
            </NavLink>
          </li>
        ))}
      </ul>
    </div>
    {soon.length > 0 && (
    <div>
      <p className="px-3 mb-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-grotesk">Roadmap</p>
      <ul className="space-y-0.5">
        {soon.map((n) => (
          <li key={n.label}>
            <div className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-muted-foreground/60 cursor-not-allowed" title="Coming in a later batch">
              <n.icon className="h-4 w-4" />
              <span className="truncate">{n.label}</span>
              <span className="ml-auto text-[8px] font-grotesk uppercase tracking-[0.14em] rounded bg-secondary px-1.5 py-0.5 border border-border">Soon</span>
            </div>
          </li>
        ))}
      </ul>
    </div>
    )}
  </nav>
);

const AdminShell = () => {
  const { pathname } = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { user, logout, impersonation } = useAuth();
  const { has, loading: permsLoading } = useAdminPerms();
  useEffect(() => { setDrawerOpen(false); }, [pathname]);

  const canSee = (p) => (Array.isArray(p) ? p.some(has) : has(p));
  const visible = live.filter((n) => permsLoading || canSee(n.perm));
  // Determine the section the user is on (longest matching path) to gate the page body.
  const current = [...live].sort((a, b) => b.to.length - a.to.length).find((n) => (n.end ? pathname === n.to : pathname.startsWith(n.to)));
  const denied = !permsLoading && current && !canSee(current.perm);

  return (
    <div className={cn('min-h-screen flex bg-background text-foreground', impersonation && 'pt-10')}>
      {/* Desktop sidebar */}
      <aside className={cn('hidden lg:flex w-[264px] shrink-0 border-r border-border flex-col bg-card/40 backdrop-blur sticky h-screen', impersonation ? 'top-10' : 'top-0')} data-testid="admin-sidebar">
        <div className="px-5 pt-5 pb-3 flex items-center justify-between">
          <Link to="/app/admin"><Logo /></Link>
        </div>
        <div className="px-4">
          <Badge variant="outline" className="gap-1.5 border-info/40 bg-info/10 text-info font-grotesk uppercase text-[10px] tracking-wider">
            <ShieldCheck className="h-3 w-3" /> Admin Console
          </Badge>
        </div>
        <AdminNav visible={visible} />
        <div className="p-3 border-t border-border space-y-1">
          <Link to="/app/settings" className="flex items-center gap-3 rounded-xl p-2 hover:bg-secondary transition-colors" data-testid="admin-profile-link">
            <Avatar className="h-9 w-9">
              <AvatarImage src={user?.avatar} alt={user?.name} />
              <AvatarFallback>{(user?.first_name || user?.name || 'A')[0]}</AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{user?.name || 'Admin'}</p>
              <p className="text-[11px] text-muted-foreground truncate">{user?.cohort || 'Bower Staff'}</p>
            </div>
          </Link>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 flex flex-col overflow-x-clip">
        <header className={cn('sticky z-30 bg-background/85 backdrop-blur border-b border-border pt-safe', impersonation ? 'top-10' : 'top-0')}>
          <div className="flex items-center gap-2 px-4 lg:px-8 py-3">
            <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden h-9 w-9 shrink-0 -ml-1" data-testid="admin-mobile-menu" aria-label="Open menu">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-[300px] bg-card border-r border-border overflow-y-auto" data-testid="admin-mobile-drawer">
                <SheetHeader className="px-5 pt-5 pb-2 text-left">
                  <SheetTitle asChild>
                    <Link to="/app/admin" className="inline-flex"><Logo /></Link>
                  </SheetTitle>
                </SheetHeader>
                <div className="px-4"><Badge variant="outline" className="gap-1.5 border-info/40 bg-info/10 text-info font-grotesk uppercase text-[10px] tracking-wider"><ShieldCheck className="h-3 w-3" /> Admin Console</Badge></div>
                <AdminNav visible={visible} />
              </SheetContent>
            </Sheet>
            <div className="lg:hidden"><Link to="/app/admin"><Logo mark /></Link></div>

            <Link to="/app" className="hidden lg:inline-flex">
              <Button variant="ghost" size="sm" className="gap-1.5 text-muted-foreground" data-testid="admin-back-to-app">
                <ArrowLeft className="h-4 w-4" /> Back to Founder OS
              </Button>
            </Link>

            <div className="ml-auto flex items-center gap-1.5 md:gap-2 shrink-0">
              <WorkspaceSwitcher />
              <ThemeToggle />
              <Button
                variant="ghost" size="icon" className="h-9 w-9" title="Sign out" data-testid="admin-signout"
                onClick={() => { logout(); window.location.href = '/signin'; }}
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </header>

        <section className="flex-1 pb-16">
          {denied ? (
            <div className="flex flex-col items-center justify-center text-center py-32 px-6" data-testid="admin-access-denied">
              <div className="h-14 w-14 rounded-2xl bg-destructive/10 text-destructive grid place-items-center mb-5"><Lock className="h-6 w-6" /></div>
              <h2 className="font-display text-3xl">No access to this section.</h2>
              <p className="text-muted-foreground mt-2 max-w-md text-sm">Your role doesn’t include the <span className="font-medium text-foreground">{current?.label}</span> permission. Ask a Super Admin to grant it under Roles (RBAC).</p>
              <Button asChild variant="outline" className="mt-6 rounded-full"><Link to="/app/admin">Back to Dashboard</Link></Button>
            </div>
          ) : (permsLoading && current && current.perm !== 'dashboard') ? (
            <div className="py-40 grid place-items-center" data-testid="admin-perms-loading"><div className="h-8 w-8 rounded-full border-2 border-border border-t-foreground animate-spin" /></div>
          ) : (
            <RouteErrorBoundary>
              <Suspense fallback={<PageLoader />}>
                <Outlet />
              </Suspense>
            </RouteErrorBoundary>
          )}
        </section>
      </main>
    </div>
  );
};

export default AdminShell;
